import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useOutlet } from 'react-router-dom';
import { motion } from 'motion/react';
import { where } from 'firebase/firestore';
import {
  FolderKanban,
  LayoutDashboard,
  ListChecks,
  Monitor,
  Moon,
  Settings,
  Sun,
  SunMoon,
  GanttChartSquare,
} from 'lucide-react';
import {
  canOrg,
  DEFAULT_USER_STATUS,
  STATUS_INACTIVITY_TIMEOUT_MS,
  USER_STATUSES,
  type OrgPermission,
  type Task,
  type UserStatus,
} from '@allemnii-pulse/shared';
import { useAuth } from '../features/auth/AuthProvider.js';
import { useOrgs } from '../features/orgs/OrgProvider.js';
import { api } from '../lib/api.js';
import { usePresencePing } from '../lib/presence.js';
import { useVisualViewportHeight } from '../lib/viewport.js';
import { InstallBanner } from '../features/pwa/InstallBanner.js';
import { useUserProfile } from '../lib/users.js';
import { StatusDot, USER_STATUS_LABEL } from '../components/UserStatus.js';
import { useCollectionGroup } from '../lib/firestore.js';
import { useTheme } from './ThemeProvider.js';
import { NotificationBell } from '../features/notifications/NotificationBell.js';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/Avatar.js';
import { Badge } from '../components/ui/Badge.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/Select.js';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from '../components/ui/Sidebar.js';

/** `permission`, when set, hides the item from anyone whose role in the current org lacks it. */
const NAV_ITEMS: {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  permission?: OrgPermission;
}[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/projects', label: 'Projects', icon: FolderKanban },
  { to: '/my-tasks', label: 'My tasks', icon: ListChecks },
  { to: '/timeline', label: 'Timeline', icon: GanttChartSquare },
  // Team activity (/team-activity, permission 'org.teamActivity.view') is hidden from the nav
  // for now, and its route in App.tsx redirects to the dashboard.
  { to: '/org-settings', label: 'Org settings', icon: Settings },
];

/** Logo mark shared by the desktop sidebar header and the mobile top bar. */
function Wordmark() {
  return (
    <div className="flex items-center gap-2.5">
      <img src="/logo.png" alt="" width="22" height="22" className="logo-pulse" />
      <span className="font-heading font-semibold text-xl text-sidebar-foreground">Pulse</span>
    </div>
  );
}

/** Count of tasks assigned to the current user that aren't done/canceled — same "open" filter
 * MyTasksPage defaults to (its "Hide completed" checkbox starts checked). Backs the sidebar
 * badge so it always matches what that page would show. */
function useMyOpenTaskCount(): number {
  const { user } = useAuth();
  const constraints = useMemo(() => (user ? [where('assigneeUid', '==', user.uid)] : []), [user]);
  const { data: tasks } = useCollectionGroup<Task>('tasks', constraints);
  return tasks.filter((t) => t.status !== 'done' && t.status !== 'canceled').length;
}

function AppNav() {
  const location = useLocation();
  const { setOpenMobile } = useSidebar();
  const { currentOrg } = useOrgs();
  const myTaskCount = useMyOpenTaskCount();
  const items = NAV_ITEMS.filter(
    (item) => !item.permission || canOrg(currentOrg?.role, item.permission),
  );

  // Close the mobile drawer after navigating. Only on an actual path change: AppNav mounts when
  // the drawer opens, so an unconditional effect would close it again the moment it appears.
  const lastPathname = useRef(location.pathname);
  useEffect(() => {
    if (lastPathname.current === location.pathname) return;
    lastPathname.current = location.pathname;
    setOpenMobile(false);
  }, [location.pathname, setOpenMobile]);

  return (
    <SidebarMenu className="gap-0.5 px-1">
      {items.map((item) => {
        const isActive =
          item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to);
        const count = item.to === '/my-tasks' ? myTaskCount : 0;
        return (
          <SidebarMenuItem key={item.to}>
            <SidebarMenuButton
              isActive={isActive}
              render={<NavLink to={item.to} end={item.to === '/'} />}
              className="relative h-[34px] rounded-lg text-sm data-active:bg-transparent data-active:font-normal"
            >
              {isActive && (
                <motion.span
                  layoutId="nav-active"
                  className="absolute inset-0 rounded-lg bg-sidebar-accent"
                  transition={{ type: 'spring', stiffness: 500, damping: 40 }}
                />
              )}
              <span
                className={`relative flex flex-1 items-center gap-2 ${isActive ? 'text-sidebar-accent-foreground' : 'text-[var(--ink-300)]'}`}
              >
                <item.icon className="h-4 w-4 shrink-0" />
                {item.label}
                {count > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-auto h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-none"
                  >
                    {count > 99 ? '99+' : count}
                  </Badge>
                )}
              </span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      })}
    </SidebarMenu>
  );
}

/** Your own status, as a badge next to your name that opens a picker. It's set by hand and
 * stays until you change it, sign out, or go STATUS_INACTIVITY_TIMEOUT_MS without activity (see
 * PresencePing). The live users/{uid} read is the source of truth; the picked value is held
 * only until that snapshot catches up, so the badge doesn't flick back in between. */
function MyStatusPicker({ uid }: { uid: string }) {
  const { data: profile } = useUserProfile(uid);
  const saved = profile?.status ?? DEFAULT_USER_STATUS;
  const [pending, setPending] = useState<UserStatus | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (pending === saved) setPending(null);
  }, [pending, saved]);

  async function change(next: UserStatus) {
    setPending(next);
    setFailed(false);
    try {
      await api.put('/me/status', { status: next });
    } catch {
      setPending(null);
      setFailed(true);
    }
  }

  return (
    <Select value={pending ?? saved} onValueChange={(v) => void change(v as UserStatus)}>
      <SelectTrigger
        aria-label="Your status"
        title={failed ? "Couldn't update your status — try again" : 'Set your status'}
        className={`h-5 w-auto gap-1 rounded-full border-[var(--ink-600)] bg-[var(--ink-800)] px-2 text-[11px] text-[var(--ink-200)] [&>svg]:h-3 [&>svg]:w-3 ${failed ? 'border-[var(--presence-busy)]' : ''}`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {USER_STATUSES.map((s) => (
          <SelectItem key={s} value={s}>
            <span className="flex items-center gap-2">
              <StatusDot status={s} /> {USER_STATUS_LABEL[s]}
            </span>
          </SelectItem>
        ))}
        <p className="max-w-[12rem] px-2 pb-1 pt-1.5 text-[11px] leading-snug text-muted">
          Switches to Offline after {STATUS_INACTIVITY_TIMEOUT_MS / 60_000} min without activity.
        </p>
      </SelectContent>
    </Select>
  );
}

/** Keeps presence/{uid} fresh while you use the app (lib/presence.ts). Lives in the shell
 * rather than in the sidebar, whose contents unmount on mobile when the sheet is closed. */
function PresencePing({ uid }: { uid: string }) {
  const { data: profile } = useUserProfile(uid);
  usePresencePing(uid, profile?.status ?? DEFAULT_USER_STATUS);
  return null;
}

function UserFooter() {
  const { user, signOutUser } = useAuth();
  const { theme, setTheme } = useTheme();

  return (
    <div className="flex flex-col gap-2">
      <Select value={theme} onValueChange={(v) => setTheme(v as typeof theme)}>
        <SelectTrigger
          aria-label="Theme"
          className="h-8 border-[var(--ink-600)] bg-[var(--ink-800)] text-xs text-[var(--ink-200)]"
        >
          <SunMoon className="h-3.5 w-3.5 shrink-0" />
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="light">
            <span className="flex items-center gap-2">
              <Sun className="h-3.5 w-3.5" /> Light
            </span>
          </SelectItem>
          <SelectItem value="dark">
            <span className="flex items-center gap-2">
              <Moon className="h-3.5 w-3.5" /> Dark
            </span>
          </SelectItem>
          <SelectItem value="system">
            <span className="flex items-center gap-2">
              <Monitor className="h-3.5 w-3.5" /> System
            </span>
          </SelectItem>
        </SelectContent>
      </Select>
      <div className="flex items-center gap-2.5 px-1">
        <Avatar className="h-7 w-7 bg-[var(--ink-600)]">
          {user?.photoURL && <AvatarImage src={user.photoURL} alt="" />}
          <AvatarFallback className="bg-[var(--ink-600)] text-[var(--ink-50)]">
            {user?.displayName?.[0] ?? user?.email?.[0]?.toUpperCase() ?? '?'}
          </AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
          <span className="w-full truncate text-xs text-sidebar-foreground">
            {user?.displayName ?? user?.email}
          </span>
          {user && <MyStatusPicker uid={user.uid} />}
        </div>
        <NavLink
          to="/settings"
          className="text-[var(--ink-400)] hover:text-sidebar-foreground"
          title="Settings"
        >
          <Settings className="h-3.5 w-3.5" />
        </NavLink>
        <button
          onClick={() => void signOutUser()}
          className="text-xs text-[var(--ink-400)] hover:text-sidebar-foreground"
          title="Sign out"
        >
          ⏻
        </button>
      </div>
    </div>
  );
}

/** The persistent shell — near-black sidebar, subtle neutral active state — matching the design canvas
 * (design/Main.dc.html). See docs/PLAN.md §4/§12. Built on the ported shadcn Sidebar
 * (components/ui/Sidebar.tsx) — see docs/PLAN.md §12 for the design source. */
export function AppShell() {
  const location = useLocation();
  // Captured here (rather than a bare <Outlet/> below) so the exiting page keeps rendering its
  // own matched route element while it fades out — a plain <Outlet/> re-subscribes to the
  // router's context and would swap to the *new* route mid-exit, flashing the incoming page
  // underneath the outgoing one for a frame before the fade-out even starts.
  const outlet = useOutlet();
  const { user } = useAuth();
  useVisualViewportHeight();

  return (
    <SidebarProvider className="h-[var(--app-height,100dvh)] min-h-0 flex-col md:flex-row">
      {user && <PresencePing uid={user.uid} />}
      <header className="flex h-[calc(3.5rem+env(safe-area-inset-top))] flex-shrink-0 items-center gap-2.5 border-b border-[var(--ink-700)] bg-[var(--ink-900)] px-[18px] pt-[env(safe-area-inset-top)] text-[var(--ink-50)] md:hidden">
        <SidebarTrigger className="-ml-1.5 text-[var(--ink-200)] hover:text-[var(--ink-50)]" />
        <Wordmark />
        <NotificationBell className="ml-auto text-[var(--ink-200)] hover:text-[var(--ink-50)]" />
      </header>
      <InstallBanner />

      <Sidebar>
        <SidebarHeader className="h-14 flex-row items-center justify-between border-b border-[var(--ink-700)] px-[18px] py-0">
          <Wordmark />
          <NotificationBell className="text-[var(--ink-200)] hover:text-[var(--ink-50)]" />
        </SidebarHeader>
        <SidebarContent>
          <AppNav />
        </SidebarContent>
        <SidebarFooter className="border-t border-[var(--ink-700)] p-3">
          <UserFooter />
        </SidebarFooter>
      </Sidebar>

      <SidebarInset className="overflow-hidden bg-bg">
        {/* This div, not `main`, is the real scroll container: it's the one with a definite
            height (h-full resolves against SidebarInset's flex-1 height) AND `overflow-auto`,
            so pages' own `h-full`/`flex-1` children get a real height to size against instead
            of silently collapsing to auto — which is what made the parent's own overflow-auto
            the one that (invisibly) ended up scrolling, several ancestors above any in-page
            sticky header. Also opacity-only: animating `y` would leave a `transform` on this
            div even at rest, which breaks `position: sticky` for every descendant.
            Fade-in only, no exit: AnimatePresence mode="wait" could get stuck with the outgoing
            page at opacity 0 (and the incoming one never mounted) when the path changed again
            mid-exit — rapid clicks, <Navigate> redirects, navigate() right after a mutation. */}
        <motion.div
          key={location.pathname}
          className="h-full overflow-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
        >
          {outlet}
          {/* Room for the iPhone home indicator under the last of a scrolling page. Project pages
              scroll inside ProjectLayout, which adds its own. */}
          {!location.pathname.startsWith('/projects/') && (
            <div aria-hidden className="h-[env(safe-area-inset-bottom)]" />
          )}
        </motion.div>
      </SidebarInset>
    </SidebarProvider>
  );
}
